import {
  PlainLink,
  Flex,
  SkeletonText,
  SkeletonShape,
  Avatar,
  FormattedRelativeTime,
  Text,
} from '@customerio/pluma-components/react';
import type { ServicesApiShippedResource } from '@/services/api/services-api';
import { useAnalytics } from '@/services/analytics';
import { useAuth } from '@/services/auth';
import { useResourceInfo } from '../hooks';
import { resourceHref } from './recents-row';
import * as styles from './recently-launched.css';

const TYPE_FALLBACK_LABELS: Record<string, string> = {
  campaign: 'a campaign',
  newsletter: 'a broadcast',
  transactional: 'a transactional message',
  in_app_broadcast: 'an in-app message',
};

interface ActivityItemProps {
  resource: ServicesApiShippedResource;
  workspaceId: number;
}

function ActivityItem({ resource, workspaceId }: ActivityItemProps) {
  const { track } = useAnalytics();
  const { name, isNotFound } = useResourceInfo(workspaceId, resource.type, resource.id);
  const { user } = useAuth();
  const isCurrentUser = resource.user_id > 0 && resource.user_id === user.id;
  const displayName = name ?? (isNotFound ? TYPE_FALLBACK_LABELS[resource.type] ?? resource.type : undefined);

  return (
    <PlainLink
      href={resourceHref(workspaceId, resource)}
      className={styles.itemLink}
      onClick={() =>
        track('home', 'recentActivityClicked', { 'Resource Type': resource.type, Action: resource.action })
      }
    >
      <div className={styles.item}>
        <Avatar name={resource.user_id > 0 ? resource.user_name : 'System'} />
        <Flex direction="column" gap="025" minW="0" flexGrow="1" flexShrink="1">
          <Text as="div" size="s">
            <Text as="span" size="s" weight="semibold">
              {isCurrentUser ? 'You' : resource.user_id > 0 ? resource.user_name : 'System'}
            </Text>{' '}
            <Text as="span" size="s" color="subtle">
              {resource.action}
            </Text>{' '}
            <Text as="span" size="s" weight="semibold">
              {displayName ?? <SkeletonShape display="inline-block" style={{ width: 80, height: 14 }} />}
            </Text>
          </Text>
          <Text size="xs" color="subtle">
            <FormattedRelativeTime date={resource.updated_at * 1000} cursor="pointer" />
          </Text>
        </Flex>
      </div>
    </PlainLink>
  );
}

interface RecentlyLaunchedProps {
  shipped: ServicesApiShippedResource[] | undefined;
  isLoading: boolean;
  workspaceId: number;
}

export function RecentlyLaunched({ shipped, isLoading, workspaceId }: RecentlyLaunchedProps) {
  if (isLoading) {
    return (
      <Flex direction="column" gap="050">
        {Array.from({ length: 3 }, (_, index) => (
          <div key={index} className={styles.item}>
            <SkeletonShape style={{ width: 32, height: 32, borderRadius: '50%' }} />
            <Flex direction="column" gap="025" minW="0" flexGrow="1" flexShrink="1">
              <SkeletonText lines={2} />
            </Flex>
          </div>
        ))}
      </Flex>
    );
  }

  if (shipped == null || shipped.length === 0) {
    return null;
  }

  return (
    <Flex direction="column" gap="050">
      {shipped.map((resource) => (
        <ActivityItem key={`${resource.type}-${resource.id}`} resource={resource} workspaceId={workspaceId} />
      ))}
    </Flex>
  );
}
